/**
 * OWNER-DAILY-EOD-REPORT-V2 -- the compact cash strip.
 *
 * Partha's five Daily/EOD cash truths, kept whole and kept SECONDARY
 * (founder ruling 2026-08-19): opening, cash revenue, bank deposit,
 * expenses, closing -- one compact row of figures, never half the page,
 * never a second cash calculation. Every figure is read from the desk's
 * own daily cash close through the existing branch-money-day truth owner.
 *
 * CASH IS CLINIC-GRAIN AND THIS PACKAGE INVENTS NO ROLLUP. When the
 * selected scope cannot truthfully produce cash -- more than one clinic in
 * view -- the strip renders the honest clinic-selection state instead of
 * summing branches whose drawers are separate books.
 *
 * UNKNOWN IS NOT ZERO. A day whose count has not been closed prints its
 * state in words; it never prints INR 0, which would assert an empty
 * drawer that nobody counted.
 *
 * PERIOD SEMANTICS LIVE IN DEFINITIONS (founder closeout 2026-08-19, item
 * 2). Over a date range, opening is the first day's and closing is the last
 * day's, while the middle three are period totals -- true, but not
 * operational copy. The explanation lives ONCE, inside the existing
 * collapsed definitions disclosure; this strip stays five figures.
 */

import type { ConsoleCashLedgerWire } from '@/dashboard/types';
import { SectionContainer } from '@/dashboard/ui';

import { inrMinor, REPORT_NO_VALUE, type ReportScope } from './report-scope';

/** Why a cash figure is unavailable, in the owner's words. */
export const CASH_STATE_LABEL: Record<string, string> = {
  pending_prior_day: "Waiting on an earlier day's count",
  no_chain: 'Day not closed',
};

export interface CashStripProps {
  readonly scope: ReportScope;
  /** Null when the scope spans more than one clinic (no rollup exists). */
  readonly ledger: ConsoleCashLedgerWire | null;
  readonly testId?: string;
}

/** One cash figure: a resolved amount, or the honest reason it is absent. */
export function cashFigure(
  minorUnits: number | null,
  state: string,
): string {
  if (minorUnits !== null) return inrMinor(minorUnits);
  return CASH_STATE_LABEL[state] ?? REPORT_NO_VALUE;
}

function CashCell({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div className="omc-cashcell" data-testid={testId}>
      <span className="omc-cashcell__label">{label}</span>
      <span className="omc-cashcell__value">{value}</span>
    </div>
  );
}

export default function CashStrip({
  scope,
  ledger,
  testId = 'report-cash',
}: CashStripProps): JSX.Element {
  if (ledger === null) {
    return (
      <SectionContainer
        title="Cash"
        count={0}
        scope={scope.label}
        defaultOpen
        testId={testId}
      >
        <p className="omc-caption" data-testid="report-cash-unavailable">
          Cash is counted per clinic. Select one clinic to see its opening,
          cash revenue, deposit, expenses and closing.
        </p>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer
      title={`Cash \u00B7 ${scope.periodLabel}`}
      count={5}
      scope={scope.label}
      stat={cashFigure(ledger.closingMinorUnits, ledger.closingState)}
      defaultOpen
      testId={testId}
    >
      <div className="omc-cashstrip" data-testid="report-cash-strip">
        <CashCell
          label="Opening"
          value={cashFigure(ledger.openingMinorUnits, ledger.state)}
          testId="report-cash-opening"
        />
        <CashCell
          label="Cash revenue"
          value={inrMinor(ledger.cashCollectedMinorUnits)}
          testId="report-cash-revenue"
        />
        <CashCell
          label="Bank deposit"
          value={inrMinor(ledger.depositsMinorUnits)}
          testId="report-cash-deposit"
        />
        <CashCell
          label="Expenses"
          value={inrMinor(ledger.expensesMinorUnits)}
          testId="report-cash-expenses"
        />
        <CashCell
          label="Closing"
          value={cashFigure(ledger.closingMinorUnits, ledger.closingState)}
          testId="report-cash-closing"
        />
      </div>
    </SectionContainer>
  );
}
